/* eslint-disable no-useless-escape */
import React, {
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useState
} from "react";

import { Box, useMultiStyleConfig } from "@chakra-ui/react";

import { useLocation } from "react-router-dom";

import { generateTestId } from "@/util/testIds";

import { Banner } from "./Banner";
import TermsOfService from "./TermsOfService";

interface SideBarContextType {
  isSideBarVisible: boolean;
  setIsSideBarVisible: (isVisible: boolean) => void;
  toggleSideBar: () => void;
  sideBarContent: ReactNode | undefined;
  setSideBarContent: (content: ReactNode) => void;
}

const SideBarContext = createContext<SideBarContextType | undefined>(undefined);

export const useSideBar = () => {
  const context = useContext(SideBarContext);
  if (!context) {
    throw new Error("useSideBar must be used within a SideBarProvider");
  }
  return context;
};

export interface AppLayoutProps {
  header: ReactNode;
  actions?: ReactNode;
  navigation?: ReactNode;
  navigationBottomPanel?: ReactNode;
}

export const AppLayout: React.FC<PropsWithChildren<AppLayoutProps>> = ({
  navigation,
  header,
  actions,
  navigationBottomPanel,
  children
}) => {
  const styles = useMultiStyleConfig("AppLayout", {});
  const [isSideBarVisible, setIsSideBarVisible] = useState(false);
  const [sideBarContent, setSideBarContent] = useState<ReactNode | undefined>(
    undefined
  );

  const appEnv = import.meta.env.VITE_BASE_APP_ENV;

  const location = useLocation();
  const isDashboard = location.pathname.match(
    /^\/company\/[a-f0-9\-]+(\/client\/[a-f0-9\-]+)?$/
  );

  const toggleSideBar = () => setIsSideBarVisible((prev) => !prev);

  return (
    <SideBarContext.Provider
      value={{
        isSideBarVisible,
        setIsSideBarVisible,
        toggleSideBar,
        sideBarContent,
        setSideBarContent
      }}
    >
      <Box
        sx={styles.wrapper}
        bgImage="url('/images/background.svg')"
        bgRepeat="no-repeat"
        bgSize="cover"
        data-testid={generateTestId.section("app-layout-wrapper")}
      >
        <Box
          sx={styles.navigation}
          data-testid={generateTestId.section("app-navigation")}
        >
          {navigation}
          <Box
            sx={styles.navigationBottomPanel}
            data-testid={generateTestId.section("navigation-bottom-panel")}
          >
            {navigationBottomPanel}
          </Box>
        </Box>
        <Box
          as="main"
          sx={{
            ...styles.page,
            transition: "width 0.3s ease-in-out",
            width: isSideBarVisible ? "calc(100% - 300px)" : "100%"
          }}
          data-testid={generateTestId.section("app-main-content")}
        >
          {actions && (
            <Box
              sx={styles.actions}
              data-testid={generateTestId.section("app-actions")}
            >
              {actions}
            </Box>
          )}
          <Box
            sx={styles.content}
            data-testid={generateTestId.section("app-content")}
          >
            {isDashboard && appEnv !== "production" && (
              <Banner
                message={`This is a ${appEnv} Environment`}
                data-testid={generateTestId.widget("environment-banner")}
              />
            )}
            <Box
              sx={styles.header}
              data-testid={generateTestId.section("app-header")}
            >
              {header}
            </Box>
            <Box data-testid={generateTestId.section("app-children")}>
              {children}
            </Box>
          </Box>
        </Box>
        <Box
          position="fixed"
          right={0}
          top={0}
          bottom={0}
          width="300px"
          bg="white"
          boxShadow="lg"
          transform={isSideBarVisible ? "translateX(0)" : "translateX(100%)"}
          transition="transform 0.3s ease-in-out"
          zIndex={1000}
          overflowY="auto"
        >
          {sideBarContent}
        </Box>
      </Box>
      <TermsOfService
        data-testid={generateTestId.section("terms-of-service")}
      />
    </SideBarContext.Provider>
  );
};
